/* eslint-disable newline-after-var */
/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { ContentItemUnion, ContentStatusType, ContentType, ContentTypeKey } from '../../../../../../../ResourcesRevised/types';
import { IoIosArrowForward, IoIosArrowBack } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { useScroll } from '../../../../../../../../libs/CustomHooks/useScroll';
import useResourceHelper from './useResourceHelper';
import { useSearchParams, useParams } from 'react-router-dom';
// Import the needed functions and types from content_graphql_api
import contentGraphQLApi from '../../../../../../../actions/resource_graphql_actions/content_graphql_api';
import { ContentStatus, ContentType as GraphQLContentType, TimePeriod, Image as GraphQLImage } from '../../../../../../../actions/resource_graphql_actions/content_graphql_types';
import useDidMountEffect from '../../../../../../../../libs/CustomHooks/UseDidMountEffect';
import ResourceShimmerCard from './ResourceShimmerCard';
// Interface for pagination state
interface PaginationState {
	total: number;
	page: number;
	limit: number;
	pages?: number;
}

const ContentListContainer = ({
	getContentApi,
	children,

	selectedItems,
	handleCheckboxChange,
	handleBulkSelectionUpdate,
	onCountUpdate,
	selectAll,
	showPath = false,
	StatusVisible = true,
	viewAll,
	handleViewAll,
	searchValue,
	type
}) =>
{
	const { type: routeType, categoryId } = useParams();
	const [ currentIndex, setCurrentIndex ] = useState(0);
	// State for storing images from API
	// const [ images, setImages ] = useState<ImageResponse[]>([]);
	const [ contentList, setContentList ] = useState<any[]>([]);
	// Loading state
	const [ isLoading, setIsLoading ] = useState(true);
	// Pagination state for API response
	const [ pagination, setPagination ] = useState<PaginationState>({
		total : 0,
		page  : 0,
		limit : 0,
		pages : 0
	});
	// Current page for pagination
	const [ page, setPage ] = useState(1);
	const itemsPerPage = 10;
	// Refs for infinite scrolling
	const lastEleRef = useRef(null);
	const loaderRef = useRef<boolean>(false);
	const [ searchParams ] = useSearchParams();
	const isUncategorized = searchParams.get('status') === 'uncategorized';
	const containerRef = useRef<HTMLDivElement>(null);
	const [ screenWidth, setScreenWidth ] = useState(window.innerWidth);
	const cardWidth = 270; // Width of each card in pixels
	const gap = 16; // Gap between cards in pixels
	const isPlaylistsPage = routeType === 'playlist';

	const sessionCompanyId = useSelector((state: any) => state?.mysession?.session?.companyId);
	const uncategorizedId = useSelector((state: any) => state?.myresource?.uncategorizedId);
	const { getContentStatus, getSortDirection, isAdminView, isFavoritesList, getDateFilterValue } = useResourceHelper();

	// Calculate number of cards that can fit in the viewport
	const cardsPerPage = useMemo(() =>
	{
		const containerWidth = screenWidth; // Accounting for padding
		const cardsPerView = Math.floor((containerWidth - gap) / (cardWidth));

		return Math.max(1, cardsPerView); // Ensure at least 1 card is shown
	}, [ screenWidth ]);
	const totalIndex = useMemo(() => Math.floor(pagination.total / cardsPerPage), [ pagination.total, cardsPerPage ]);
	const totalCardsTillCurrentIndex = useMemo(() => cardsPerPage + cardsPerPage * currentIndex, [ cardsPerPage, currentIndex ]);

	// Update screen width on resize
	useEffect(() =>
	{
		const handleResize = () =>
		{
			// setScreenWidth(window.innerWidth);
			setScreenWidth(containerRef.current?.clientWidth || 0);
		};

		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Effect to handle selectAll state change
	useEffect(() =>
	{
		if (selectAll && contentList.length > 0)
		{
			// If selectAll is true, add all image IDs to selection
			const allImageIds = contentList.map((image) => image._id);

			handleBulkSelectionUpdate(allImageIds, true);
		}
	}, [ selectAll, contentList ]);

	// Report the count to parent component when images change
	// useEffect(() =>
	// {
	// 	onCountUpdate(contentList.length);
	// }, [ contentList ]);

	// // Fetch images with pagination
	const fetchContent = useCallback(async () =>
	{
		try
		{
			setIsLoading(true);
			loaderRef.current = true;

			const memberFilter = searchParams.get('memberFilter');
			const dateFilter = searchParams.get('dateFilter') as TimePeriod;
			const contentStatus = getContentStatus();

			let filter;

			if (searchValue && searchValue.trim() !== '')
			{
				filter = {
					// contentType : GraphQLContentType.IMAGE,
					page,
					limit   : itemsPerPage,
					filters : {
						...(isUncategorized
							? uncategorizedId && { categoryIds: [ uncategorizedId ] }
							: categoryId && { categoryIds: [ categoryId ] }),
						status : contentStatus as unknown as ContentStatus,
						...(isFavoritesList && { favorited: true })
					}
				};
			}
			else
			{
				filter = {
					page,
					limit   : itemsPerPage,
					filters : {
						// types     : [ GraphQLContentType.IMAGE ],
						status : contentStatus as unknown as ContentStatus,
						// ...(categoryId && { categoryIds: [ categoryId ] }),
						...(isUncategorized
							? uncategorizedId && { categoryIds: [ uncategorizedId ] }
							: categoryId && { categoryIds: [ categoryId ] }),
						...(memberFilter && memberFilter !== 'all' && { createdBy: memberFilter }),
						timeRange : (dateFilter && dateFilter !== 'ALL_TIME') ? getDateFilterValue(dateFilter) : undefined,
						...(isFavoritesList && { favorited: true })
					},
					...(getSortDirection() && { sort: getSortDirection() })
				};
			}

			const response = await getContentApi(filter);

			if (response && response.data)
			{
				// If it's the first page, replace the data, otherwise append
				const newImages = page === 1
					? response.data as unknown as any
					: [ ...contentList, ...(response.data as unknown as any) ];

				setContentList(newImages);
				if (onCountUpdate)
				{
					onCountUpdate(newImages.length);
				}
				// Store pagination information
				if (response.pagination)
				{
					setPagination({
						total : response.pagination.total || 0,
						page  : response.pagination.page || 0,
						limit : response.pagination.limit || 0,
						pages : response.pagination.pages || Math.ceil(response.pagination.total / itemsPerPage) || 0
					});
				}
			}
		}
		catch (error)
		{
			console.error('Error fetching images:', error);
		}
		finally
		{
			setIsLoading(false);
			loaderRef.current = false;
		}
	}, [
		searchParams,
		getContentStatus,
		searchValue,
		page,
		itemsPerPage,
		isUncategorized,
		uncategorizedId,
		categoryId,
		isFavoritesList,
		getDateFilterValue,
		getSortDirection,
		getContentApi,
		contentList
	]);

	// Use the custom scroll hook for infinite scrolling
	useScroll({
		lastEleRef,
		loaderRef,
		count : viewAll ? pagination.total : 0,
		data  : viewAll ? contentList : [],
		setPage
	});

	// Initial fetch and when dependencies change
	useEffect(() =>
	{
		if (sessionCompanyId)
		{
			fetchContent();
		}
	}, [ page ]);

	// Handle filter changes via search params
	useDidMountEffect(() =>
	{
		if (sessionCompanyId)
		{
			page > 1
				? setPage(1)
				: fetchContent();
		}
	}, [ sessionCompanyId, searchParams, categoryId, searchValue, uncategorizedId ]);

	// Reset page when searchValue changes
	useEffect(() =>
	{
		if (searchValue !== undefined)
		{
			setPage(1);
			setContentList([]); // Clear existing images when search changes
		}
	}, [ searchValue ]);

	// Listen for content status changes (publish/unpublish)
	useEffect(() =>
	{
		const handleContentStatusChange = (event: CustomEvent) =>
		{
			const { contentType } = event.detail;
			// Only refresh if the content type matches

			if (contentType.toLowerCase() === type.toLowerCase())
			{
				// setContentList([]);
				page > 1
					? setPage(1)
					: fetchContent();
			}
		};

		window.addEventListener('contentStatusChanged', handleContentStatusChange as any);

		return () =>
		{
			window.removeEventListener('contentStatusChanged', handleContentStatusChange as any);
		};
	}, [ type, fetchContent ]);

	const keys = {
		Images    : 'images',
		Documents : 'documents',
		Articles  : 'articles',
		Playlists : 'playlists',
		Podcasts  : 'podcasts',
		Videos    : 'videos'
	};
	// Handler for toggling viewAll state
	const toggleViewAllLocal = () =>
	{
		if (handleViewAll)
		{
			const keyToPass = viewAll ? 'all' : keys[type];

			handleViewAll(keyToPass);
		}
	};

	// Local arrow navigation handler
	const handleArrowClick = (direction: 'next' | 'prev') =>
	{
		if (direction === 'next')
		{
			if (cardsPerPage + cardsPerPage * (currentIndex + 1) >= contentList.length)
			{
				setPage(page + 1);
			}
			setCurrentIndex((prev) => Math.min(prev + 1, totalIndex));
		}
		else
		{
			setCurrentIndex((prev) => Math.max(prev - 1, 0));
		}
	};

	// No content to display
	if (contentList.length === 0 || (page === 1 && isLoading))
	{
		// If loading on first page, show shimmer
		if (isLoading && page === 1)
		{
			return (
				<Grid item py={1} width={'100%'}>
					<Stack
						pb={1.5}
						direction={'row'}
						alignItems={'center'}
						justifyContent={'space-between'}
						width={'100%'}
					>
						<Stack direction={'row'} alignItems={'center'} spacing={0.5}>
							<Typography fontWeight={500}>{type}</Typography>
						</Stack>
					</Stack>
					<ResourceShimmerCard totalCards={viewAll ? 5 : 4} shouldScrollable={!viewAll} />
				</Grid>
			);
		}

		// If not loading and no content, return null (parent will handle "No Data" message)
		if (!isLoading && contentList.length === 0)
		{
			return null;
		}
	}

	const location = window.location.pathname;
	const resourcePaths = [ 'resource/images', 'resource/videos', 'resource/documents', 'resource/articles', 'resource/podcasts' ];
	const path = resourcePaths.some((resourcePath) => location.includes(resourcePath));
	console.log('path:::::::::::::', path);

	const displayImages = !viewAll
		? contentList.slice(
			currentIndex * cardsPerPage,
			totalCardsTillCurrentIndex
		)
		: [];

	const ContentCards = (contentProp: any) =>
	{
		return React.Children.map(children, (child) =>
		{
			// if (React.isValidElement(child) && child.type === ContentCards)
			if (React.isValidElement(child))
			{
			// Add extra props to only <C />
				return React.cloneElement(child, contentProp);
			}

			return child;
		});
	};

	return (
		<Grid item py={1} width={'100%'} ref={containerRef}
			onLoad={(e) =>
			{
				const node = e.currentTarget as HTMLElement;

				setScreenWidth(node.clientWidth);
			}}
		>
			<Stack
				pb={1.5}
				direction={'row'}
				alignItems={'center'}
				justifyContent={'space-between'}
				width={'100%'}
			>
				<Stack direction={'row'} alignItems={'center'} spacing={0.5}>
					<Typography fontWeight={500}>{type}</Typography>
					<Typography fontWeight={500}>
						({pagination.total})
					</Typography>
				</Stack>

				{!isPlaylistsPage && !path &&
				<Button
					variant='text'
					size='small'
					sx={{ textTransform: 'none !important' }}
					onClick={toggleViewAllLocal}
				>
					{!viewAll ? 'View All' : 'View Less'}
				</Button>}
			</Stack>

			{viewAll
				? (
					<Grid container spacing={1}>
						{contentList.map((content, index) => content?._id && (
							<Grid item xs={12} sm={6} md={4} lg={3} key={content._id || index}>
								{ContentCards({
									contentData         : content,
									likeIcon            : location.includes('favourates'),
									moreIcon            : true,
									disableInteractions : false,
									// type,
									// icon                : content.url,
									// categories          : content.categoryIds?.[0],
									// user                : content.createdBy?.name,
									watermarkTxt        : '',
									description         : content.description || content.title,
									Status              : (content.status as ContentStatusType) ?? 'Draft',
									GridView            : true,
									showPath,
									isChecked           : selectedItems.includes(content._id),
									// section             : 'images',
									StatusVisible,
									// ContentView,
									hoveredCardId       : selectedItems,
									handleCheckboxChange,
									onMoveSuccess       : () =>
									{
										setPage(1);
										setContentList([]);
										fetchContent();
									},
									isAdminView : { isAdminView }
								})}
							</Grid>
						))}

						{/* Element for intersection observer */}
						{!isLoading && contentList.length > 0 && contentList.length < pagination.total && (
							<div ref={lastEleRef} style={{ height: '2px', width: '100%' }} />
						)}
					</Grid>
				)
				: (
					<Box
						id='auto-scrollId'
						sx={{
							position             : 'relative',
							width                : '100%',
							'&:hover .nav-arrow' : {
								opacity    : 1,
								visibility : 'visible'
							}
						}}
						display='flex'
					>
						<Box
							pb={1.5}
							sx={{
								display                : 'flex',
								position               : 'relative',
								overflowY              : 'visible',
								gap                    : '8px',
								width                  : '100%',
								scrollbarGutter        : 'stable',
								'&::-webkit-scrollbar' : { display: 'none' },
								'-ms-overflow-style'   : 'none',
								'scrollbar-width'      : 'none',
								touchAction            : 'pan-x'
							}}
							flexDirection={'row'}
						>
							{currentIndex !== 0 && (
								<Box
									className='nav-arrow'
									position='absolute'
									left='0px'
									top='2px'
									borderRadius='8px'
									height='95%'
									width={44}
									alignItems='center'
									justifyContent='center'
									display='flex'
									zIndex={9}
									sx={{
										cursor     : 'pointer',
										background : 'linear-gradient(to right, rgba(12, 12, 12, 0.78), rgba(46, 46, 46, 0.5), rgba(155, 155, 155, 0))',
										opacity    : 0,
										visibility : 'hidden',
										transition : 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
										color      : '#fff'
									}}
									onClick={() => handleArrowClick('prev')}
								>
									<IoIosArrowBack fontSize={20} />
								</Box>
							)}

							{displayImages.map((content, index) => (
								<Box
									key={content._id || index}
									sx={{
										position   : 'relative',
										transition : 'transform 0.3s ease-in-out',
										'&:active' : {
											transform : 'scale(1.05)'
										},
										flexShrink : 0,
										width      : cardWidth
									}}
								>
									{ContentCards({
										contentData         : content,
										ImageUrls           : content.files?.map((image: any) => image?.url),
										ind                 : index,
										likeIcon            : location.includes('favourates'),
										moreIcon            : true,
										disableInteractions : false,
										// type                : 'Images',
										// icon                : content.url,
										// categories          : content.categoryIds?.[0],
										// user                : content.createdBy?.name,
										watermarkTxt        : '',
										description         : content.description || content.title,
										Status              : (content.status as ContentStatusType) ?? 'Draft',
										GridView            : false,
										showPath,
										isChecked           : selectedItems.includes(content._id),
										// section             : 'images',
										StatusVisible,
										hoveredCardId       : selectedItems,
										handleCheckboxChange,
										onMoveSuccess       : () =>
										{
											setPage(1);
											setContentList([]);
											fetchContent();
										},
										isAdminView : { isAdminView }
									})}
								</Box>
							))}
							{contentList.length <= pagination.total && (totalCardsTillCurrentIndex < contentList.length) && (
								<Box
									className='nav-arrow'
									position='absolute'
									right='0px'
									top='2px'
									onClick={() => handleArrowClick('next')}
									borderRadius='8px'
									height='95%'
									width={44}
									alignItems='center'
									justifyContent='center'
									display='flex'
									zIndex={9}
									sx={{
										cursor     : 'pointer',
										background : 'linear-gradient(to left, rgba(12, 12, 12, 0.78), rgba(46, 46, 46, 0.5), rgba(155, 155, 155, 0))',
										opacity    : 0,
										visibility : 'hidden',
										transition : 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
										color      : '#fff'
									}}
								>
									<IoIosArrowForward fontSize={20} />
								</Box>
							)}
						</Box>
					</Box>
				)}

			{/* Loading indicator */}
			{isLoading && page > 1 && (
				<Box sx={{ width: '100%', textAlign: 'center', p: 2 }}>
					<Typography>Loading more...</Typography>
				</Box>
			)}
		</Grid>
	);
};

// Add display name
ContentListContainer.displayName = 'ContentListContainer';

export default ContentListContainer;